import { UserApiResponse, ApiResponse } from './ApiResponses';
import { BASE_API_URL } from '../Enums';


/**
 * Add a new user to the database.
 * @param {Object} userToRegister - The object containing all of the user's
 * information
 * @param {(string|undefined)} userToRegister.firstName
 * @param {(string|undefined)} userToRegister.lastName
 * @param {(string|undefined)} userToRegister.email
 * @param {(string|undefined)} userToRegister.password - This is hashed in the
 * frontend
 * @param {(string|undefined)} userToRegister.major
 * @param {(string|undefined)} userToRegister.numberOfSemestersToSignUpFor
 * @returns {UserApiResponse} containing if the search
 *                            was successful or error data
 */
export async function registerUser(userToRegister) {
  let status = new UserApiResponse();
  const {
    firstName,
    lastName,
    email,
    password,
    major,
    numberOfSemestersToSignUpFor,
    captchaToken
  } = userToRegister;
  const url = new URL('/api/Auth/register', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        password,
        major,
        numberOfSemestersToSignUpFor,
        captchaToken
      })
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
    status.responseData = err.response;
  }
  return status;
}

/**
 * Logs in the user with their email and password. Also updates the last login
 * date for the user.
 * @param {string} email The email of the user
 * @param {string} password The password of the user
 * @returns {UserApiResponse} Contains the generated JWT token or any error data
 */
export async function loginUser(email, password) {
  let status = new UserApiResponse();
  const url = new URL('/api/Auth/login', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });
    const result = await res.json();
    if (res.ok) {
      status.token = result.token;
      window.location.reload();
      return status;
    }
    status.error = true;
    status.responseData = result.message;
  } catch(err) {
    status.responseData = 'Backend may be down, check with the dev team! Error was: ' + err.message;
    status.error = true;
  }
  return status;
}

/**
 * Checks if the user is signed in by evaluating a jwt token in local storage.
 * @returns {UserApiResponse} Containing information for
 * whether the user is signed or not
 */
export async function checkIfUserIsSignedIn() {
  let status = new UserApiResponse();

  const token = window.localStorage
    ? window.localStorage.getItem('jwtToken')
    : '';

  // If there is not token in local storage,
  // we cant do anything and return
  if (!token) {
    status.error = true;
    return status;
  }

  const url = new URL('/api/Auth/verify', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (res.ok) {
      const result = await res.json();
      status.responseData = result;
      status.token = token;
    }
  } catch(err) {
    status.error = true;
    status.responseData = err;
  }
  return status;
}

/**
 * Verify a user's email after the visiting the link sent to their email.
 * @param {string} email - The user's email
 * @param {string} hashedId - A hashed value of the user's mongoDB _id field
 * @returns {ApiResponse} Containing any error information related to the
 * request
 */
export async function validateVerificationEmail(email, hashedId) {
  let status = new ApiResponse();
  const url = new URL('/api/Auth/validateVerificationEmail', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, hashedId })
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
    status.responseData = err.response;
  }
  return status;
}

/**
 * Reset a user's password after the visiting the link sent to their email.
 * @param {string} hashedId - A hashed value of the user's mongoDB _id field
 * @param {string} password - The user's password
 * @returns {ApiResponse} Containing any error information related to the
 * request
 */
export async function resetPassword(password, hashedId, resetToken) {
  let status = new ApiResponse();
  const url = new URL('/api/Auth/resetPassword', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password, hashedId, resetToken })
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
    status.responseData = err.response;
  }
  return status;
}

export async function validatePasswordReset(resetToken) {
  let status = new ApiResponse();
  const url = new URL('/api/Auth/validatePasswordReset', BASE_API_URL);
  try {
    const res = await fetch(url.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ resetToken })
    });
    if (!res.ok) {
      status.error = true;
    }
  } catch(err) {
    status.error = true;
    status.responseData = err.response;
  }
  return status;
}
